import { Entity, formatTicker, SnakeMeta } from '@api';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import type { TerminalChartEntity } from '@terminal/views/turbo-chart/utils/chart-utils/types';

import styles from './SeriesLegendLabel.module.scss';

const LEGEND_LOGO_SIZE = 20;

export interface SeriesLegendLabelProps {
  entity: TerminalChartEntity;
  snakeMeta?: SnakeMeta;
  onTickerClick?: () => void;
  onIndicatorClick?: () => void;
  /** Tooltip text for the ticker button (e.g. `"Change data"`). */
  changeDataLabel?: string;
  /** Tooltip text for the indicator button (e.g. `"Change indicator"`). */
  changeIndicatorLabel?: string;
}

/**
 * Renders the ticker row the same way the original chart does: asset logo,
 * `TICKER:EXCHANGE` button, separator dot, and indicator button. Consumers
 * hand this React node to chartv2 via `series.legendLabel`.
 */
export const SeriesLegendLabel = ({
  entity,
  snakeMeta,
  onTickerClick,
  onIndicatorClick,
  changeDataLabel,
  changeIndicatorLabel,
}: SeriesLegendLabelProps) => {
  const indicator = snakeMeta?.name.english;
  const tickerLabel = (
    entity.formattedTicker ||
    formatTicker(entity) ||
    entity.name
  ).toUpperCase();

  return (
    <span className={styles.root}>
      <Tooltip
        label={changeDataLabel}
        inPortal
        disabled={!changeDataLabel || !onTickerClick}
      >
        <button
          type="button"
          className={styles.tickerButton}
          onClick={onTickerClick}
          aria-label={changeDataLabel}
          data-testid={`series-legend-ticker-${entity.tag}`}
        >
          <span className={styles.logoSlot} aria-hidden>
            <AssetLogoContainer
              // `TerminalChartEntity extends Entity` with UI-only extras;
              // cast required for the StrictUnion prop shape.
              entity={entity as Entity}
              size={LEGEND_LOGO_SIZE}
            />
          </span>
          <span className={styles.ticker}>{tickerLabel}</span>
        </button>
      </Tooltip>
      {indicator && (
        <>
          <span className={styles.separatorDot} aria-hidden />
          <Tooltip
            label={changeIndicatorLabel}
            inPortal
            disabled={!changeIndicatorLabel || !onIndicatorClick}
          >
            <button
              type="button"
              className={styles.indicatorButton}
              onClick={onIndicatorClick}
              aria-label={changeIndicatorLabel}
              data-testid={`series-legend-indicator-${entity.tag}`}
            >
              {indicator}
            </button>
          </Tooltip>
        </>
      )}
    </span>
  );
};
